import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { id } from 'date-fns/locale';
import { castDraft, produce } from 'immer';
import { useCallback, useMemo } from 'react';
import isEqual from 'react-fast-compare';
import { useAppContext } from 'src/contexts/AppContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import { useContentContext } from 'src/contexts/ContentContext';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { FormatContentIds } from 'src/utils/constants/formatContentIdDataMap';
import { ReportTypesV2 } from 'src/utils/reportsUtils';
import {
  ReportV2ConfigClient,
  ReportV2ConfigOfCommissionReportV2RequestAndReportWidgetTypesNone,
  ReportV2ConfigOfCostOfGoodsSoldReportV2RequestAndReportWidgetTypesNone,
  ReportV2ConfigOfInventoryReportV2RequestAndReportWidgetTypesInventory,
  ReportV2ConfigOfPurchaseOrderLineItemReportV2RequestAndReportWidgetTypesNone,
  ReportV2ConfigOfPurchaseReportV2RequestAndReportWidgetTypesNone,
  ReportV2ConfigOfSaleLineItemReportV2RequestAndReportWidgetTypesNone,
  ReportV2ConfigOfSalesReportV2RequestAndReportWidgetTypesNone,
  ReportV2ConfigOfSaleTotalCostAdjustmentReportV2RequestAndReportWidgetTypesNone,
  ReportV2ConfigOfSoldTicketReportV2RequestAndReportWidgetTypesNone,
  ReportV2ConfigOfTicketGroupReportV2RequestAndReportWidgetTypesNone,
  ReportV2ConfigOverride,
} from 'src/WebApiController';

export const REPORT_CONFIG_V2_SCHEMA_VERSION = '2.0.0';

/**
 * Map report config settings to their data types.
 *
 * Add to this type if new settings are added.
 */
type ReportConfigMap = {
  [ReportTypesV2.InventoryV2]: ReportV2ConfigOfInventoryReportV2RequestAndReportWidgetTypesInventory;
  [ReportTypesV2.SaleV2]: ReportV2ConfigOfSalesReportV2RequestAndReportWidgetTypesNone;
  [ReportTypesV2.Commission]: ReportV2ConfigOfCommissionReportV2RequestAndReportWidgetTypesNone;
  [ReportTypesV2.Purchase]: ReportV2ConfigOfPurchaseReportV2RequestAndReportWidgetTypesNone;
  [ReportTypesV2.SaleLineItem]: ReportV2ConfigOfSaleLineItemReportV2RequestAndReportWidgetTypesNone;
  [ReportTypesV2.PurchaseOrderLineItem]: ReportV2ConfigOfPurchaseOrderLineItemReportV2RequestAndReportWidgetTypesNone;
  [ReportTypesV2.SoldTicket]: ReportV2ConfigOfSoldTicketReportV2RequestAndReportWidgetTypesNone;
  [ReportTypesV2.CostOfGoodsSold]: ReportV2ConfigOfCostOfGoodsSoldReportV2RequestAndReportWidgetTypesNone;
  [ReportTypesV2.SaleCostAdjustments]: ReportV2ConfigOfSaleTotalCostAdjustmentReportV2RequestAndReportWidgetTypesNone;
  [ReportTypesV2.TicketGroup]: ReportV2ConfigOfTicketGroupReportV2RequestAndReportWidgetTypesNone;
};

/**
 * Internal type to unify the different report config types.
 */
type ReportConfigTypeBase =
  | ReportV2ConfigOfInventoryReportV2RequestAndReportWidgetTypesInventory
  | ReportV2ConfigOfSalesReportV2RequestAndReportWidgetTypesNone
  | ReportV2ConfigOfCommissionReportV2RequestAndReportWidgetTypesNone
  | ReportV2ConfigOfPurchaseReportV2RequestAndReportWidgetTypesNone
  | ReportV2ConfigOfSaleLineItemReportV2RequestAndReportWidgetTypesNone
  | ReportV2ConfigOfPurchaseOrderLineItemReportV2RequestAndReportWidgetTypesNone
  | ReportV2ConfigOfSoldTicketReportV2RequestAndReportWidgetTypesNone
  | ReportV2ConfigOfCostOfGoodsSoldReportV2RequestAndReportWidgetTypesNone
  | ReportV2ConfigOfSaleTotalCostAdjustmentReportV2RequestAndReportWidgetTypesNone
  | ReportV2ConfigOfTicketGroupReportV2RequestAndReportWidgetTypesNone;

type ReportConfigType<S extends ReportTypesV2> = ReportConfigMap[S];

/**
 * Helper type for consumers of this hook to use for typing report configs more easily.
 */
export type ReportConfigV2 = ReportConfigTypeBase & {
  reportType: ReportTypesV2;
};

type UseReportConfigsOptions = {
  reportType: ReportTypesV2;
};

type ReportConfigInput = Omit<
  ReportConfigV2,
  'schemaVersion' | 'reportId' | 'reportType'
>;

/**
 * Data and functions returned by the hook to help with CRUD of report config with different types
 */
type UseReportConfigsInternal = {
  reportConfigs: ReportConfigV2[];
  isLoading: boolean;
  isFetching: boolean;
  deleteReportConfig: (reportId: number) => void;
  upsertReportConfig: (
    reportId: number | null,
    updatedConfig: Partial<ReportConfigInput>,
    isTemporary?: boolean
  ) => Promise<number | null>;
  upsertReportOverride: (
    configOverride: ReportV2ConfigOverride,
    originalReportConfig:
      | ReportV2ConfigOfInventoryReportV2RequestAndReportWidgetTypesInventory
      | ReportV2ConfigOfSalesReportV2RequestAndReportWidgetTypesNone
      | ReportV2ConfigOfCommissionReportV2RequestAndReportWidgetTypesNone
      | ReportV2ConfigOfPurchaseReportV2RequestAndReportWidgetTypesNone
      | ReportV2ConfigOfSaleLineItemReportV2RequestAndReportWidgetTypesNone
      | ReportV2ConfigOfSoldTicketReportV2RequestAndReportWidgetTypesNone
      | ReportV2ConfigOfPurchaseOrderLineItemReportV2RequestAndReportWidgetTypesNone
      | ReportV2ConfigOfCostOfGoodsSoldReportV2RequestAndReportWidgetTypesNone
      | ReportV2ConfigOfTicketGroupReportV2RequestAndReportWidgetTypesNone
  ) => void;
  getReportConfigNameForDuplicate: (reportName: string) => string;
};

const addTimezoneOffsetToSaleReportConfig = (
  reportNoOffset: ReportV2ConfigOfSalesReportV2RequestAndReportWidgetTypesNone
): ReportV2ConfigOfSalesReportV2RequestAndReportWidgetTypesNone => {
  return {
    ...reportNoOffset,
    request: {
      ...reportNoOffset.request,
      filters: {
        ...reportNoOffset.request.filters,
        timezoneOffsetMins: new Date().getTimezoneOffset(),
      },
    },
  };
};

const addTimezoneOffsetToListingReportConfig = (
  reportNoOffset: ReportV2ConfigOfInventoryReportV2RequestAndReportWidgetTypesInventory
): ReportV2ConfigOfInventoryReportV2RequestAndReportWidgetTypesInventory => {
  return {
    ...reportNoOffset,
    request: {
      ...reportNoOffset.request,
      filters: {
        ...reportNoOffset.request.filters,
        timezoneOffsetMins: new Date().getTimezoneOffset(),
      },
    },
  };
};
const addTimezoneOffsetToCommissionReportConfig = (
  reportNoOffset: ReportV2ConfigOfCommissionReportV2RequestAndReportWidgetTypesNone
): ReportV2ConfigOfCommissionReportV2RequestAndReportWidgetTypesNone => {
  return {
    ...reportNoOffset,
    request: {
      ...reportNoOffset.request,
      filters: {
        ...reportNoOffset.request.filters,
        timezoneOffsetMins: new Date().getTimezoneOffset(),
      },
    },
  };
};

const addTimezoneOffsetToPurchaseReportConfig = (
  reportNoOffset: ReportV2ConfigOfPurchaseReportV2RequestAndReportWidgetTypesNone
): ReportV2ConfigOfPurchaseReportV2RequestAndReportWidgetTypesNone => {
  return {
    ...reportNoOffset,
    request: {
      ...reportNoOffset.request,
      filters: {
        ...reportNoOffset.request.filters,
        timezoneOffsetMins: new Date().getTimezoneOffset(),
      },
    },
  };
};

const addTimezoneOffsetToSaleLineItemReportConfig = (
  reportNoOffset: ReportV2ConfigOfSaleLineItemReportV2RequestAndReportWidgetTypesNone
): ReportV2ConfigOfSaleLineItemReportV2RequestAndReportWidgetTypesNone => {
  return {
    ...reportNoOffset,
    request: {
      ...reportNoOffset.request,
      filters: {
        ...reportNoOffset.request.filters,
        timezoneOffsetMins: new Date().getTimezoneOffset(),
      },
    },
  };
};

const addTimezoneOffsetToSoldTicketReportConfig = (
  reportNoOffset: ReportV2ConfigOfSoldTicketReportV2RequestAndReportWidgetTypesNone
): ReportV2ConfigOfSoldTicketReportV2RequestAndReportWidgetTypesNone => {
  return {
    ...reportNoOffset,
    request: {
      ...reportNoOffset.request,
      filters: {
        ...reportNoOffset.request.filters,
        timezoneOffsetMins: new Date().getTimezoneOffset(),
      },
    },
  };
};

const addTimezoneOffsetToPurchaseOrderLineItemReportConfig = (
  reportNoOffset: ReportV2ConfigOfPurchaseOrderLineItemReportV2RequestAndReportWidgetTypesNone
): ReportV2ConfigOfPurchaseOrderLineItemReportV2RequestAndReportWidgetTypesNone => {
  return {
    ...reportNoOffset,
    request: {
      ...reportNoOffset.request,
      filters: {
        ...reportNoOffset.request.filters,
        timezoneOffsetMins: new Date().getTimezoneOffset(),
      },
    },
  };
};

const addTimezoneOffsetToCostOfGoodsSoldReportConfig = (
  reportNoOffset: ReportV2ConfigOfCostOfGoodsSoldReportV2RequestAndReportWidgetTypesNone
): ReportV2ConfigOfCostOfGoodsSoldReportV2RequestAndReportWidgetTypesNone => {
  return {
    ...reportNoOffset,
    request: {
      ...reportNoOffset.request,
      filters: {
        ...reportNoOffset.request.filters,
        timezoneOffsetMins: new Date().getTimezoneOffset(),
      },
    },
  };
};

const addTimezoneOffsetToSaleTotalCostAdjustmentReportConfig = (
  reportNoOffset: ReportV2ConfigOfSaleTotalCostAdjustmentReportV2RequestAndReportWidgetTypesNone
): ReportV2ConfigOfSaleTotalCostAdjustmentReportV2RequestAndReportWidgetTypesNone => {
  return {
    ...reportNoOffset,
    request: {
      ...reportNoOffset.request,
      filters: {
        ...reportNoOffset.request.filters,
        timezoneOffsetMins: new Date().getTimezoneOffset(),
      },
    },
  };
};

const addTimezoneOffsetToTicketGroupReportConfig = (
  reportNoOffset: ReportV2ConfigOfTicketGroupReportV2RequestAndReportWidgetTypesNone
): ReportV2ConfigOfTicketGroupReportV2RequestAndReportWidgetTypesNone => {
  return {
    ...reportNoOffset,
    request: {
      ...reportNoOffset.request,
      filters: {
        ...reportNoOffset.request.filters,

        timezoneOffsetMins: new Date().getTimezoneOffset(),
      },
    },
  };
};

/**
 * Hook to help with CRUD of report config with different types
 * @param options
 * @returns
 */
export function useReportConfigsV2Internal<S extends ReportTypesV2>({
  reportType,
}: UseReportConfigsOptions): UseReportConfigsInternal {
  const { showErrorDialog, trackError } = useClientLogContext();
  const { formattedContentResolver } = useContentContext();
  const { activeAccountWebClientConfig } = useAppContext();
  const reportClient = useMemo(
    () => new ReportV2ConfigClient(activeAccountWebClientConfig),
    [activeAccountWebClientConfig]
  );

  const queryClient = useQueryClient();
  const queryKey = useMemo(
    () => [
      'useReportConfigsV2',
      activeAccountWebClientConfig.activeAccountId,
      reportType,
    ],
    [activeAccountWebClientConfig.activeAccountId, reportType]
  );

  // Query to get all report configs
  const reportConfigQuery = useQuery<ReportConfigType<S>[] | null>({
    queryKey,
    queryFn: async () => {
      if (activeAccountWebClientConfig?.activeAccountId == null) {
        return null;
      }

      if (reportType === ReportTypesV2.InventoryV2) {
        return (await reportClient.getAvailableInventoryReportConfig())
          .filter((r) => r.schemaVersion === REPORT_CONFIG_V2_SCHEMA_VERSION)
          .map(addTimezoneOffsetToListingReportConfig) as ReportConfigType<S>[];
      } else if (reportType === ReportTypesV2.Commission) {
        return (await reportClient.getAvailableCommissionReportConfig())
          .filter((r) => r.schemaVersion === REPORT_CONFIG_V2_SCHEMA_VERSION)
          .map(
            addTimezoneOffsetToCommissionReportConfig
          ) as ReportConfigType<S>[];
      } else if (reportType === ReportTypesV2.Purchase) {
        return (await reportClient.getAvailablePurchaseReportConfig())
          .filter((r) => r.schemaVersion === REPORT_CONFIG_V2_SCHEMA_VERSION)
          .map(
            addTimezoneOffsetToPurchaseReportConfig
          ) as ReportConfigType<S>[];
      } else if (reportType === ReportTypesV2.SaleLineItem) {
        return (await reportClient.getAvailableSaleLineItemReportConfig())
          .filter((r) => r.schemaVersion === REPORT_CONFIG_V2_SCHEMA_VERSION)
          .map(
            addTimezoneOffsetToSaleLineItemReportConfig
          ) as ReportConfigType<S>[];
      } else if (reportType === ReportTypesV2.SoldTicket) {
        return (await reportClient.getAvailableSoldTicketReportConfig())
          .filter((r) => r.schemaVersion === REPORT_CONFIG_V2_SCHEMA_VERSION)
          .map(
            addTimezoneOffsetToSoldTicketReportConfig
          ) as ReportConfigType<S>[];
      } else if (reportType === ReportTypesV2.PurchaseOrderLineItem) {
        return (
          await reportClient.getAvailablePurchaseOrderLineItemReportConfig()
        )
          .filter((r) => r.schemaVersion === REPORT_CONFIG_V2_SCHEMA_VERSION)
          .map(
            addTimezoneOffsetToPurchaseOrderLineItemReportConfig
          ) as ReportConfigType<S>[];
      } else if (reportType === ReportTypesV2.CostOfGoodsSold) {
        return (await reportClient.getAvailableCostOfGoodsSoldReportConfig())
          .filter((r) => r.schemaVersion === REPORT_CONFIG_V2_SCHEMA_VERSION)
          .map(
            addTimezoneOffsetToCostOfGoodsSoldReportConfig
          ) as ReportConfigType<S>[];
      } else if (reportType === ReportTypesV2.SaleCostAdjustments) {
        return (
          await reportClient.getAvailableSaleTotalCostAdjustmentReportConfig()
        )
          .filter((r) => r.schemaVersion === REPORT_CONFIG_V2_SCHEMA_VERSION)
          .map(
            addTimezoneOffsetToSaleTotalCostAdjustmentReportConfig
          ) as ReportConfigType<S>[];
      } else if (reportType === ReportTypesV2.TicketGroup) {
        return (await reportClient.getAvailableTicketGroupReportConfig())
          .filter((r) => r.schemaVersion === REPORT_CONFIG_V2_SCHEMA_VERSION)
          .map(
            addTimezoneOffsetToTicketGroupReportConfig
          ) as ReportConfigType<S>[];
      } else {
        return (await reportClient.getAvailableSaleReportConfig())
          .filter((r) => r.schemaVersion === REPORT_CONFIG_V2_SCHEMA_VERSION)
          .map(addTimezoneOffsetToSaleReportConfig) as ReportConfigType<S>[];
      }
    },

    enabled: activeAccountWebClientConfig?.activeAccountId != null,
    meta: {
      onError: (err: ErrorTypes) => {
        trackError('ReportV2ConfigClient.getAvailableReportConfig', err, {
          trackErrorData: { reportType },
        });
      },
    },
    networkMode: 'offlineFirst',
    refetchOnWindowFocus: false,
  });

  const _reportConfigs = useMemo(() => {
    if (reportConfigQuery.data == null) {
      return [];
    }
    return reportConfigQuery.data;
  }, [reportConfigQuery.data]);

  // Mutation to upsert report config
  const upsertMutation = useMutation({
    mutationFn: async ({
      value,
      isTemporary,
    }: {
      value: ReportConfigType<S>;
      isTemporary?: boolean;
    }) => {
      if (isTemporary) {
        // Do nothing, just let onMutate handle the optimistic update
        return value.reportId!;
      }

      if (reportType === ReportTypesV2.InventoryV2) {
        return await reportClient.upsertInventoryReportConfig(
          value as ReportV2ConfigOfInventoryReportV2RequestAndReportWidgetTypesInventory
        );
      } else if (reportType === ReportTypesV2.Purchase) {
        return await reportClient.upsertPurchaseReportConfig(
          value as ReportV2ConfigOfPurchaseReportV2RequestAndReportWidgetTypesNone
        );
      } else if (reportType === ReportTypesV2.SaleLineItem) {
        return await reportClient.upsertSaleLineItemReportConfig(
          value as ReportV2ConfigOfSaleLineItemReportV2RequestAndReportWidgetTypesNone
        );
      } else if (reportType === ReportTypesV2.SoldTicket) {
        return await reportClient.upsertSoldTicketReportConfig(
          value as ReportV2ConfigOfSoldTicketReportV2RequestAndReportWidgetTypesNone
        );
      } else if (reportType === ReportTypesV2.PurchaseOrderLineItem) {
        return await reportClient.upsertPurchaseOrderLineItemReportConfig(
          value as ReportV2ConfigOfPurchaseOrderLineItemReportV2RequestAndReportWidgetTypesNone
        );
      } else if (reportType === ReportTypesV2.CostOfGoodsSold) {
        return await reportClient.upsertCostOfGoodsSoldReportConfig(
          value as ReportV2ConfigOfCostOfGoodsSoldReportV2RequestAndReportWidgetTypesNone
        );
      } else if (reportType === ReportTypesV2.SaleCostAdjustments) {
        return await reportClient.upsertSaleTotalCostAdjustmentReportConfig(
          value as ReportV2ConfigOfSaleTotalCostAdjustmentReportV2RequestAndReportWidgetTypesNone
        );
      } else if (reportType === ReportTypesV2.TicketGroup) {
        return await reportClient.upsertTicketGroupReportConfig(
          value as ReportV2ConfigOfTicketGroupReportV2RequestAndReportWidgetTypesNone
        );
      } else if (reportType === ReportTypesV2.Commission) {
        throw new Error(
          'Creating commission report config is not supported yet'
        );
      }
      return await reportClient.upsertSaleReportConfig(
        value as ReportV2ConfigOfSalesReportV2RequestAndReportWidgetTypesNone
      );
    },
    // optimistic ui
    // Reference: https://tanstack.com/query/v3/docs/react/guides/optimistic-updates
    onMutate: ({ value }) => {
      queryClient.cancelQueries({ queryKey });
      const prevValue =
        queryClient.getQueryData<ReportConfigType<S>[]>(queryKey);

      const newValue =
        value.reportId == null
          ? [...(prevValue ?? []), value]
          : prevValue?.map((r) => (r.reportId === value.reportId ? value : r));
      queryClient.setQueryData<ReportConfigType<S>[]>(queryKey, newValue);
      return { prevValue };
    },
    onError: (err: ErrorTypes, { value }, context) => {
      queryClient.setQueryData(queryKey, context?.prevValue);
      showErrorDialog('ReportV2ConfigClient.upsertReportConfig', err, {
        trackErrorData: { id, value },
      });
    },
    onSettled: (_data, _error, { isTemporary }) => {
      if (!isTemporary) {
        queryClient.invalidateQueries({ queryKey });
      }
    },
  });

  const upsertOverrideMutation = useMutation({
    mutationFn: async ({
      configOverride,
      originalReportConfig,
    }: {
      configOverride: ReportV2ConfigOverride;
      originalReportConfig:
        | ReportV2ConfigOfInventoryReportV2RequestAndReportWidgetTypesInventory
        | ReportV2ConfigOfSalesReportV2RequestAndReportWidgetTypesNone
        | ReportV2ConfigOfCommissionReportV2RequestAndReportWidgetTypesNone
        | ReportV2ConfigOfPurchaseReportV2RequestAndReportWidgetTypesNone
        | ReportV2ConfigOfSaleLineItemReportV2RequestAndReportWidgetTypesNone
        | ReportV2ConfigOfSoldTicketReportV2RequestAndReportWidgetTypesNone
        | ReportV2ConfigOfPurchaseOrderLineItemReportV2RequestAndReportWidgetTypesNone
        | ReportV2ConfigOfCostOfGoodsSoldReportV2RequestAndReportWidgetTypesNone
        | ReportV2ConfigOfSaleTotalCostAdjustmentReportV2RequestAndReportWidgetTypesNone
        | ReportV2ConfigOfTicketGroupReportV2RequestAndReportWidgetTypesNone;
    }) => {
      const reportConfigId = originalReportConfig.reportId!;
      configOverride.schemaVersion = originalReportConfig.schemaVersion;

      // Set override to null if it is the same as the original report config
      // and the original report config does not have an override.
      if (!originalReportConfig.hasMetricsOverride) {
        if (
          isEqual(
            originalReportConfig.request.aggregations,
            configOverride.request.aggregations
          )
        ) {
          configOverride.request.aggregations = null;
        }
        if (
          isEqual(
            originalReportConfig.request.rowGroupings,
            configOverride.request.rowGroupings
          )
        ) {
          configOverride.request.rowGroupings = null;
        }
      }

      await reportClient.upsertReportConfigOverride(
        reportConfigId,
        configOverride as ReportV2ConfigOverride
      );
    },
    // optimistic ui
    // Reference: https://tanstack.com/query/v3/docs/react/guides/optimistic-updates
    onMutate: ({ originalReportConfig, configOverride }) => {
      queryClient.cancelQueries({ queryKey });
      const prevValue =
        queryClient.getQueryData<ReportConfigType<S>[]>(queryKey);

      const newReportConfig = {
        ...originalReportConfig,
        request: {
          ...originalReportConfig.request,
          aggregations:
            configOverride.request.aggregations ??
            originalReportConfig.request.aggregations,
          rowGroupings:
            configOverride.request.rowGroupings ??
            originalReportConfig.request.rowGroupings,
        },
      } as ReportConfigType<S>;

      const newValue = prevValue?.map((r) =>
        r.reportId === newReportConfig.reportId ? newReportConfig : r
      );
      queryClient.setQueryData<ReportConfigType<S>[]>(queryKey, newValue);
      return { prevValue };
    },
    onError: (
      err: ErrorTypes,
      { originalReportConfig, configOverride },
      context
    ) => {
      queryClient.setQueryData(queryKey, context?.prevValue);
      showErrorDialog('reportClient.upsertReportConfig', err, {
        trackErrorData: { id, originalReportConfig, configOverride },
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Mutation to upsert report config
  const upsertReportOverride = useCallback(
    async (
      configOverride: ReportV2ConfigOverride,
      originalReportConfig:
        | ReportV2ConfigOfInventoryReportV2RequestAndReportWidgetTypesInventory
        | ReportV2ConfigOfSalesReportV2RequestAndReportWidgetTypesNone
        | ReportV2ConfigOfCommissionReportV2RequestAndReportWidgetTypesNone
        | ReportV2ConfigOfPurchaseReportV2RequestAndReportWidgetTypesNone
        | ReportV2ConfigOfPurchaseOrderLineItemReportV2RequestAndReportWidgetTypesNone
        | ReportV2ConfigOfSaleLineItemReportV2RequestAndReportWidgetTypesNone
        | ReportV2ConfigOfSoldTicketReportV2RequestAndReportWidgetTypesNone
        | ReportV2ConfigOfCostOfGoodsSoldReportV2RequestAndReportWidgetTypesNone
        | ReportV2ConfigOfSaleTotalCostAdjustmentReportV2RequestAndReportWidgetTypesNone
        | ReportV2ConfigOfTicketGroupReportV2RequestAndReportWidgetTypesNone
    ) => {
      return await upsertOverrideMutation.mutateAsync({
        configOverride: {
          ...configOverride,
          schemaVersion: REPORT_CONFIG_V2_SCHEMA_VERSION,
        },
        originalReportConfig,
      });
    },
    [upsertOverrideMutation]
  );

  const upsertReportConfig = (
    reportConfig: ReportConfigType<S>,
    isTemporary?: boolean
  ) => {
    return upsertMutation.mutateAsync({ value: reportConfig, isTemporary });
  };

  // Mutation to delete report config
  const deleteMutation = useMutation({
    mutationFn: async ({ reportId }: { reportId: number }) => {
      return await reportClient.deleteReportConfig(reportId);
    },
    // optimistic ui
    // Reference: https://tanstack.com/query/v3/docs/react/guides/optimistic-updates
    onMutate: ({ reportId }) => {
      queryClient.cancelQueries({ queryKey });
      const prevValue =
        queryClient.getQueryData<ReportConfigType<S>[]>(queryKey);

      queryClient.setQueryData<ReportConfigType<S>[]>(
        queryKey,
        prevValue?.filter((r) => r.reportId !== reportId)
      );
      return { prevValue };
    },
    onError: (err: ErrorTypes, { reportId }, context) => {
      queryClient.setQueryData(queryKey, context?.prevValue);
      showErrorDialog('ReportV2ConfigClient.deleteReportConfig', err, {
        trackErrorData: { id, reportId },
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const deleteReportConfig = (reportId: number) => {
    deleteMutation.mutateAsync({ reportId });
  };

  // Array of report config only used within the hook that has the type
  // which can be directly set to the user setting.
  const reportConfigsInternal: ReportConfigType<S>[] = _reportConfigs;

  const reportConfigs: ReportConfigV2[] = reportConfigsInternal.map(
    (r) =>
      ({
        ...r,
        reportType,
      }) satisfies ReportConfigV2
  );

  const getReportConfigNameForDuplicate = useCallback(
    (reportName: string) => {
      const reportNames = reportConfigsInternal.map((r) => r.reportName);
      const defaultName = formattedContentResolver(
        FormatContentIds[FormatContentId.CopyOf].id,
        reportName,
        FormatContentIds[FormatContentId.CopyOf].defaultValue
      ) as string;
      if (!reportNames.includes(defaultName)) {
        return defaultName;
      }

      let newName = defaultName;
      let i = 2;
      while (reportNames.includes(newName)) {
        newName = `${defaultName} (${i})`;
        i++;
      }
      return newName;
    },
    [formattedContentResolver, reportConfigsInternal]
  );

  return {
    reportConfigs,
    isLoading: reportConfigQuery.isLoading,
    isFetching: reportConfigQuery.isFetching,
    deleteReportConfig: (reportId: number) => {
      deleteReportConfig(reportId);
    },
    upsertReportConfig: (
      reportId: number | null,
      configInput: Partial<ReportConfigInput>,
      isTemporary?: boolean
    ) => {
      // Create new
      if (reportId == null) {
        // Force conversion is applied here because the type of the report config
        // Depends on the consumer to pass in the right set of widgets and filter types.
        const newConfig = castDraft({
          ...configInput,
          schemaVersion: REPORT_CONFIG_V2_SCHEMA_VERSION,
          reportId: null,
          requiredRowGroupingNames: null, // NOTE: we never want to set this - it is something on the default report config (readonly) and don't want to set it on the user report config
        } as ReportConfigType<S>);

        return upsertReportConfig(newConfig as ReportConfigType<S>);
      }

      // Update existing
      const configToUpdate = reportConfigsInternal.find(
        (r) => r.reportId === reportId
      );
      if (!configToUpdate) {
        return new Promise((resolve) => resolve(null));
      }
      const initDraft = castDraft(configToUpdate as ReportConfigType<S>);

      const updatedDraft = produce(initDraft, (draft) => {
        if (configInput.reportName != null) {
          draft.reportName = configInput.reportName;
        }

        draft.ownerDisplayName = configInput.ownerDisplayName ?? '';
        draft.widgets = castDraft(configInput.widgets ?? null);

        draft.request = castDraft(configInput.request!);
        draft.sellerUserIdsToShare = configInput.sellerUserIdsToShare ?? [];
        draft.roleIdsToShare = configInput.roleIdsToShare ?? [];
        draft.viewableFilterItemIds = configInput.viewableFilterItemIds ?? [];
        draft.editableFilterItemIds = configInput.editableFilterItemIds ?? [];
        draft.hiddenFilterItemIds = configInput.hiddenFilterItemIds ?? [];
        draft.requiredRowGroupingNames = null; // NOTE: we never want to set this - it is something on the default report config (readonly) and don't want to set it on the user report config
        draft.originReportId = configInput.originReportId ?? null;
      });

      return upsertReportConfig(
        updatedDraft as ReportConfigType<S>,
        isTemporary
      );
    },
    upsertReportOverride,
    getReportConfigNameForDuplicate,
  };
}
